import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { PrimaryActionButton, SecondaryActionButton, StonePanel } from '@aztec-realms/ui';

import { useVerticalSliceQuery } from './data/use-vertical-slice';
import { visualFixtureName } from './data/vertical-slice';
import { useJourneySafety } from './journey-safety';
import { usePresentationSettings } from './presentation-settings';
import { useVisualReady } from './visual-ready';

export const ONBOARDING_COMPLETE_KEY = 'aztec-realms:onboarding-complete';
export const ONBOARDING_GUIDE_KEY = 'aztec-realms:onboarding-guide-v2';

type PracticeStone = 'inner' | 'outer';

interface GuideDraft {
  version: 2;
  step: number;
  discoveryRevealed: boolean;
  practiceDecodeStep: number;
  practiceSelectedStone: PracticeStone;
  serverCheckRevealed: boolean;
  buildingChoice: string;
  settlementName: string;
}

const initialDraft: GuideDraft = {
  version: 2,
  step: 0,
  discoveryRevealed: false,
  practiceDecodeStep: 0,
  practiceSelectedStone: 'inner',
  serverCheckRevealed: false,
  buildingChoice: '',
  settlementName: 'Lake of First Light',
};

const guideSteps = [
  { eyebrow: 'World', label: 'See the promise', title: 'The horizon holds forgotten signs' },
  {
    eyebrow: 'Explore',
    label: 'Reveal a fragment',
    title: 'Find one sign without chasing the screen',
  },
  {
    eyebrow: 'Decode',
    label: 'Restore a channel',
    title: 'Make knowledge flow through carved stone',
  },
  {
    eyebrow: 'Reward',
    label: 'Understand verification',
    title: 'The realm confirms what you recovered',
  },
  { eyebrow: 'Build', label: 'Choose a first change', title: 'Turn knowledge into a living place' },
  { eyebrow: 'World', label: 'Begin the live loop', title: 'Return to the world with a purpose' },
] as const;

function safeStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function readGuideDraft(replay: boolean): GuideDraft {
  if (replay) return initialDraft;
  const value = safeStorage()?.getItem(ONBOARDING_GUIDE_KEY);
  if (!value) return initialDraft;
  try {
    const parsed = JSON.parse(value) as Partial<GuideDraft>;
    if (parsed.version !== 2) return initialDraft;
    return {
      version: 2,
      step: Math.min(guideSteps.length - 1, Math.max(0, Number(parsed.step) || 0)),
      discoveryRevealed: parsed.discoveryRevealed === true,
      practiceDecodeStep: Math.min(3, Math.max(0, Number(parsed.practiceDecodeStep) || 0)),
      practiceSelectedStone: parsed.practiceSelectedStone === 'outer' ? 'outer' : 'inner',
      serverCheckRevealed: parsed.serverCheckRevealed === true,
      buildingChoice: typeof parsed.buildingChoice === 'string' ? parsed.buildingChoice : '',
      settlementName:
        typeof parsed.settlementName === 'string' && parsed.settlementName.trim().length >= 2
          ? parsed.settlementName.slice(0, 40)
          : initialDraft.settlementName,
    };
  } catch {
    return initialDraft;
  }
}

function replayReturnPath(search: string): '/settings' | '/decode' | '/explore' {
  const requested = new URLSearchParams(search).get('return');
  return requested === '/decode' || requested === '/explore' ? requested : '/settings';
}

export function guidedLoopActive(search: string): boolean {
  return new URLSearchParams(search).get('guide') === '1';
}

export function guidedPath(path: '/' | '/explore' | '/decode', enabled: boolean): string {
  return enabled ? `${path}?guide=1` : path;
}

export function shouldOfferFirstTimeGuide({
  completed,
  dismissed,
  deterministic,
}: {
  completed: boolean;
  dismissed: boolean;
  deterministic: boolean;
}): boolean {
  return !completed && !dismissed && !deterministic;
}

function ExplorePractice({ revealed, onReveal }: { revealed: boolean; onReveal: () => void }) {
  return (
    <figure className="guide-practice guide-practice--explore">
      <div
        aria-label="A code-native map example with a nearby fragment and no precise route history"
        className="guide-map-example"
        role="group"
      >
        <span aria-hidden="true" className="guide-map-example__water" />
        <span aria-hidden="true" className="guide-map-example__path" />
        <button
          aria-label={revealed ? 'Nearby fragment revealed' : 'Reveal the nearby fragment'}
          aria-pressed={revealed}
          className="guide-map-example__discovery"
          onClick={onReveal}
          type="button"
        >
          <span aria-hidden="true">✦</span>
        </button>
      </div>
      <figcaption>
        {revealed
          ? 'Fragment revealed. When moving quickly, discoveries queue without asking you to tap.'
          : 'Select the quiet pulse. Explore records eligible game events; it does not publish your trail.'}
      </figcaption>
    </figure>
  );
}

function DecodePractice({
  step,
  selectedStone,
  feedback,
  onSelect,
  onRotate,
}: {
  step: number;
  selectedStone: PracticeStone;
  feedback: string;
  onSelect: (stone: PracticeStone) => void;
  onRotate: (direction: 'clockwise' | 'counter_clockwise') => void;
}) {
  const solved = step >= 3;
  const expectedStone: PracticeStone = step === 0 ? 'inner' : 'outer';
  const instruction =
    step === 0
      ? 'Select the inner source stone, then turn it clockwise.'
      : step === 1
        ? 'Select the outer bridge stone, then turn it clockwise once.'
        : step === 2
          ? 'Keep the outer bridge selected and turn it clockwise once more.'
          : 'Both rings now form one continuous path to the gold seal.';
  const innerRotation = step >= 1 ? 0 : -90;
  const outerRotation = step >= 3 ? 0 : step === 2 ? -90 : 180;
  return (
    <figure className="guide-practice guide-practice--decode" data-solved={solved || undefined}>
      <div className="guide-decode-instruction" role="status">
        <span className="realm-screen__eyebrow">Guided turn {Math.min(step + 1, 3)} of 3</span>
        <strong>{instruction}</strong>
      </div>
      <div
        aria-label={`Two-ring practice glyph with seven stones. Energy ${
          solved
            ? 'reaches the gold seal'
            : step === 0
              ? 'is held at the center'
              : step === 1
                ? 'reaches the seam between rings'
                : 'enters the outer bridge but stops before the seal'
        }.`}
        className="guide-decode-board"
        role="img"
        style={
          {
            '--guide-inner-rotation': `${innerRotation}deg`,
            '--guide-outer-rotation': `${outerRotation}deg`,
          } as CSSProperties
        }
      >
        <svg aria-hidden="true" viewBox="0 0 240 240">
          <circle className="guide-decode-board__well" cx="120" cy="120" r="112" />
          <circle
            className="guide-decode-board__stones guide-decode-board__stones--outer"
            cx="120"
            cy="120"
            pathLength="360"
            r="88"
          />
          <circle
            className="guide-decode-board__stones guide-decode-board__stones--inner"
            cx="120"
            cy="120"
            pathLength="360"
            r="52"
          />
          <g className="guide-decode-board__channel guide-decode-board__channel--inner">
            <path d="M120 120 H169" />
            <path d="M139 120 L155 104" />
          </g>
          <g className="guide-decode-board__channel guide-decode-board__channel--outer">
            <path d="M166 120 H218" />
            <path d="M190 120 L207 103" />
          </g>
          <path
            className="guide-decode-board__energy guide-decode-board__energy--inner"
            d="M120 120 H169"
          />
          <path
            className="guide-decode-board__energy guide-decode-board__energy--outer"
            d="M166 120 H218"
          />
          <circle className="guide-decode-board__source" cx="120" cy="120" r="13" />
          <path className="guide-decode-board__seal" d="M226 111 L235 120 L226 129 L217 120 Z" />
          <circle className="guide-decode-board__seam" cx="169" cy="120" r="4" />
        </svg>
      </div>
      <div aria-label="Practice stone selector" className="guide-decode-stones" role="group">
        {(['inner', 'outer'] as const).map((stone) => (
          <button
            aria-pressed={selectedStone === stone}
            data-expected={!solved && expectedStone === stone ? 'true' : undefined}
            key={stone}
            onClick={() => onSelect(stone)}
            type="button"
          >
            <strong>{stone === 'inner' ? 'Inner source stone' : 'Outer bridge stone'}</strong>
            <small>{stone === 'inner' ? 'Ring 1 · center feed' : 'Ring 2 · gold seal path'}</small>
          </button>
        ))}
      </div>
      <div aria-label="Practice rotation controls" className="guide-decode-turns" role="group">
        <button disabled={solved} onClick={() => onRotate('counter_clockwise')} type="button">
          <span aria-hidden="true">↶</span> Turn selected counter-clockwise
        </button>
        <button disabled={solved} onClick={() => onRotate('clockwise')} type="button">
          Turn selected clockwise <span aria-hidden="true">↷</span>
        </button>
      </div>
      <ol aria-label="Practice energy path" className="guide-decode-path">
        {['Center awake', 'Inner seam', 'Outer channel', 'Gold seal'].map((label, index) => (
          <li data-live={index <= step ? 'true' : undefined} key={label}>
            {label}
          </li>
        ))}
      </ol>
      <figcaption aria-live="polite">
        {feedback ||
          'Start at the source. A useful turn must extend the currently glowing path; disconnected stones cannot carry energy yet.'}
      </figcaption>
    </figure>
  );
}

function RewardPractice({ revealed, onReveal }: { revealed: boolean; onReveal: () => void }) {
  return (
    <figure className="guide-practice guide-practice--reward">
      <ol aria-label="Authoritative reward path" className="guide-authority-path">
        <li data-complete="true">Your ordered moves</li>
        <li data-complete={revealed || undefined}>Server replays the puzzle</li>
        <li data-complete={revealed || undefined}>Ledger and Codex update</li>
      </ol>
      <button className="guide-practice__action" onClick={onReveal} type="button">
        {revealed ? 'Server verification explained' : 'Ask how the reward is verified'}
      </button>
      <figcaption aria-live="polite">
        {revealed
          ? 'Only a valid server replay can grant durable resources or Codex progress. This practice grants nothing.'
          : 'A glowing solved board is presentation, never permission to credit a reward.'}
      </figcaption>
    </figure>
  );
}

function BuildPractice({
  choice,
  onChoice,
}: {
  choice: string;
  onChoice: (choice: string) => void;
}) {
  return (
    <fieldset className="guide-build-example">
      <legend>Choose a practice blueprint</legend>
      {[
        ['solar-shrine', 'Solar Shrine', 'Opens a brighter civic terrace.'],
        ['garden', 'Water Garden', 'Makes the lakeshore visibly flourish.'],
        ['codex-house', 'Codex House', 'Preserves restored glyph knowledge.'],
      ].map(([value, label, description]) => (
        <label key={value}>
          <input
            checked={choice === value}
            name="practice-building"
            onChange={() => onChoice(value ?? '')}
            type="radio"
            value={value}
          />
          <span>
            <strong>{label}</strong>
            <small>{description}</small>
          </span>
        </label>
      ))}
      <small>
        This selects a teaching example only. The live Build step uses server-owned costs.
      </small>
    </fieldset>
  );
}

export function GuideCoachmark({
  step,
  title,
  children,
  serverStep,
  serverCompleted,
}: {
  step: string;
  title: string;
  children: ReactNode;
  serverStep: number;
  serverCompleted: boolean;
}) {
  return (
    <aside aria-labelledby="live-guide-title" className="live-guide-coachmark">
      <span className="realm-screen__eyebrow">Guided path · {step}</span>
      <h2 id="live-guide-title">{title}</h2>
      <div>{children}</div>
      <p className="live-guide-coachmark__server" role="status">
        {serverCompleted
          ? 'Core loop confirmed by the server.'
          : `Server onboarding milestones: ${Math.min(8, serverStep)} of 8.`}
      </p>
      <div className="live-guide-coachmark__links">
        <Link to={{ pathname: '/guide', search: '?replay=1&return=/explore' }}>Restart guide</Link>
        <Link to="/explore">Exit guide</Link>
      </div>
    </aside>
  );
}

export function OnboardingScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const settings = usePresentationSettings();
  const journey = useJourneySafety();
  const bootstrap = useVerticalSliceQuery();
  const search = new URLSearchParams(location.search);
  const replay = search.get('replay') === '1' || location.pathname === '/guide';
  const [draft, setDraft] = useState<GuideDraft>(() => readGuideDraft(replay));
  const [practiceFeedback, setPracticeFeedback] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const current = guideSteps[draft.step] ?? guideSteps[0];
  const visualState =
    visualFixtureName() === 'world-glimpse' ? 'onboarding-world-glimpse' : undefined;
  useVisualReady(visualState, true);

  useEffect(() => {
    safeStorage()?.setItem(ONBOARDING_GUIDE_KEY, JSON.stringify(draft));
  }, [draft]);

  useEffect(() => {
    headingRef.current?.focus();
  }, [draft.step]);

  const canContinue =
    draft.step === 1
      ? draft.discoveryRevealed
      : draft.step === 2
        ? draft.practiceDecodeStep >= 3
        : draft.step === 3
          ? draft.serverCheckRevealed
          : draft.step === 4
            ? draft.buildingChoice.length > 0 && draft.settlementName.trim().length >= 2
            : true;

  function finishGuide(guided: boolean) {
    safeStorage()?.setItem(ONBOARDING_COMPLETE_KEY, 'true');
    safeStorage()?.setItem('aztec-realms:settlement-name-draft', draft.settlementName.trim());
    safeStorage()?.removeItem(ONBOARDING_GUIDE_KEY);
    const fixture = search.get('fixture');
    if (!guided && replay) {
      navigate(replayReturnPath(location.search));
      return;
    }
    const params = new URLSearchParams();
    if (guided) params.set('guide', '1');
    if (fixture === 'scenario-a') params.set('fixture', fixture);
    navigate(`/explore${params.size > 0 ? `?${params.toString()}` : ''}`);
  }

  function continueGuide() {
    if (draft.step === guideSteps.length - 1) {
      finishGuide(true);
      return;
    }
    if (!canContinue) return;
    setDraft((value) => ({ ...value, step: Math.min(guideSteps.length - 1, value.step + 1) }));
  }

  function selectPracticeStone(stone: PracticeStone) {
    setDraft((value) => ({ ...value, practiceSelectedStone: stone }));
    const expected: PracticeStone = draft.practiceDecodeStep === 0 ? 'inner' : 'outer';
    setPracticeFeedback(
      stone === expected
        ? `${stone === 'inner' ? 'Inner source' : 'Outer bridge'} selected. Now inspect the groove and make the shown clockwise turn.`
        : draft.practiceDecodeStep === 0
          ? 'That outer stone is not connected to the source yet. Start with the inner groove that touches the center.'
          : 'The inner groove already reaches the seam. Continue on the outer ring where the light currently stops.',
    );
  }

  function rotatePracticeStone(direction: 'clockwise' | 'counter_clockwise') {
    const expected: PracticeStone = draft.practiceDecodeStep === 0 ? 'inner' : 'outer';
    if (draft.practiceDecodeStep >= 3) return;
    if (draft.practiceSelectedStone !== expected) {
      setPracticeFeedback(
        expected === 'inner'
          ? 'No change: the outer bridge has no energy yet. Select the inner source stone first.'
          : 'No change: the inner path is already connected. Select the outer bridge where the glow stops.',
      );
      return;
    }
    if (direction !== 'clockwise') {
      setPracticeFeedback(
        'No change: that direction turns the carved exit away from the next seam. Follow the groove clockwise for this step.',
      );
      return;
    }
    const nextStep = draft.practiceDecodeStep + 1;
    setDraft((value) => ({ ...value, practiceDecodeStep: nextStep }));
    setPracticeFeedback(
      nextStep === 1
        ? 'Good turn: the inner groove now carries energy from the center to the ring seam. The gold seal stays dark because the outer bridge is still disconnected.'
        : nextStep === 2
          ? 'Good turn: the outer groove now catches the energy, but its exit still points sideways. One more clockwise turn is needed.'
          : 'Solved: the second outer turn joins the final seam. Energy reaches the gold seal because both ring channels now form one continuous path.',
    );
  }

  if (journey.active) {
    return (
      <main className="onboarding-screen onboarding-screen--journey" id="main-content">
        <StonePanel className="onboarding-journey-lock" ornament={2}>
          <span className="realm-screen__eyebrow">Journey Mode active</span>
          <h1>Keep the screen quiet while moving</h1>
          <p role="status">
            The interactive guide, Decode practice and Build practice stay locked until the active
            Journey ends. Discoveries continue to queue safely.
          </p>
          <PrimaryActionButton
            disabled={journey.phase === 'stopping'}
            loading={journey.phase === 'stopping'}
            onClick={() => void journey.stopJourney()}
          >
            Stop when safely stationary
          </PrimaryActionButton>
          {journey.error && (
            <>
              <p className="inline-error" role="alert">
                {journey.error}
              </p>
              <SecondaryActionButton
                disabled={journey.phase === 'stopping'}
                onClick={() => void journey.cancelJourney()}
              >
                Cancel Journey · no reward
              </SecondaryActionButton>
            </>
          )}
        </StonePanel>
      </main>
    );
  }

  return (
    <main
      className="onboarding-screen"
      data-motif={current.eyebrow.toLowerCase()}
      data-reduced-motion={settings.reducedMotion || undefined}
      data-visual-ready={visualState ? 'true' : undefined}
      data-visual-state={visualState}
      id="main-content"
    >
      <a className="ar-skip-link" href="#onboarding-copy">
        Skip to guide
      </a>
      <div aria-hidden="true" className="onboarding-world">
        <span className="onboarding-world__sun" />
        <span className="onboarding-world__mountains" />
        <span className="onboarding-world__water" />
        <span className="onboarding-world__city" />
        <span className="onboarding-world__garden onboarding-world__garden--one" />
        <span className="onboarding-world__garden onboarding-world__garden--two" />
      </div>
      <section aria-label="How to play" className="onboarding-card" id="onboarding-copy">
        <header className="onboarding-card__heading">
          <span className="realm-screen__eyebrow">{current.eyebrow}</span>
          <span className="onboarding-card__duration">5–8 minute guided path</span>
          <h1 ref={headingRef} tabIndex={-1}>
            {current.title}
          </h1>
        </header>
        <ol aria-label="Guide progress" className="onboarding-progress">
          {guideSteps.map((item, index) => (
            <li
              aria-current={index === draft.step ? 'step' : undefined}
              data-complete={index < draft.step || undefined}
              key={`${item.eyebrow}-${index}`}
              title={item.label}
            >
              <span className="ar-visually-hidden">
                Step {index + 1}: {item.label}
              </span>
            </li>
          ))}
        </ol>

        <div className="onboarding-step-copy">
          {draft.step === 0 && (
            <>
              <p>
                Explore nearby paths, restore one radial glyph, receive a server-verified reward,
                and use it to make your first visible settlement change.
              </p>
              <ul className="guide-promise-list">
                <li>No wallet or purchase is required.</li>
                <li>Journey Mode becomes passive while you move quickly.</li>
                <li>Captions and companion controls teach every essential action.</li>
              </ul>
            </>
          )}
          {draft.step === 1 && (
            <ExplorePractice
              onReveal={() => setDraft((value) => ({ ...value, discoveryRevealed: true }))}
              revealed={draft.discoveryRevealed}
            />
          )}
          {draft.step === 2 && (
            <DecodePractice
              feedback={practiceFeedback}
              onRotate={rotatePracticeStone}
              onSelect={selectPracticeStone}
              selectedStone={draft.practiceSelectedStone}
              step={draft.practiceDecodeStep}
            />
          )}
          {draft.step === 3 && (
            <RewardPractice
              onReveal={() => setDraft((value) => ({ ...value, serverCheckRevealed: true }))}
              revealed={draft.serverCheckRevealed}
            />
          )}
          {draft.step === 4 && (
            <>
              <BuildPractice
                choice={draft.buildingChoice}
                onChoice={(buildingChoice) => setDraft((value) => ({ ...value, buildingChoice }))}
              />
              <label className="onboarding-name-field" htmlFor="settlement-name">
                <span>Settlement name draft</span>
                <input
                  autoComplete="off"
                  id="settlement-name"
                  maxLength={40}
                  minLength={2}
                  onChange={(event) =>
                    setDraft((value) => ({ ...value, settlementName: event.target.value }))
                  }
                  required
                  value={draft.settlementName}
                />
              </label>
            </>
          )}
          {draft.step === 5 && (
            <div className="guide-ready-state">
              <ol aria-label="Live guided loop">
                <li>
                  <strong>Explore</strong>
                  <span>Start safely and secure one fragment.</span>
                </li>
                <li>
                  <strong>Decode</strong>
                  <span>Rotate channels, then ask the server to verify.</span>
                </li>
                <li>
                  <strong>Reward</strong>
                  <span>See the authoritative ledger and Codex result.</span>
                </li>
                <li>
                  <strong>Build</strong>
                  <span>Begin a real upgrade and return to the World.</span>
                </li>
              </ol>
              <p role="status">
                {bootstrap.data?.profile.onboardingCompleted
                  ? 'Your server-confirmed core loop is already complete. Replay remains available for practice.'
                  : `Your server has confirmed ${bootstrap.data?.profile.onboardingStep ?? 0} of 8 onboarding milestones.`}
              </p>
            </div>
          )}
        </div>

        <div className="onboarding-actions">
          <SecondaryActionButton
            disabled={draft.step === 0}
            onClick={() => setDraft((value) => ({ ...value, step: Math.max(0, value.step - 1) }))}
          >
            Back
          </SecondaryActionButton>
          <PrimaryActionButton disabled={!canContinue || bootstrap.isError} onClick={continueGuide}>
            {draft.step === 0
              ? 'Begin the journey'
              : draft.step === guideSteps.length - 1
                ? 'Start guided Explore'
                : 'Continue'}
          </PrimaryActionButton>
        </div>
        <button className="onboarding-skip" onClick={() => finishGuide(false)} type="button">
          {replay ? 'Close guide' : 'Skip guided tour'}
        </button>
        {bootstrap.isPending && (
          <span className="onboarding-bootstrap" role="status">
            Reading your server onboarding milestones…
          </span>
        )}
        {bootstrap.isError && (
          <span className="onboarding-bootstrap" role="alert">
            Player bootstrap is unavailable. The guide remains readable, but live progress needs a
            connection.
          </span>
        )}
        <p className="onboarding-caption">
          {settings.reducedMotion
            ? 'Reduced motion is active. Examples use instant state changes and captions.'
            : 'Every animated teaching example includes a persistent text caption.'}
        </p>
      </section>
    </main>
  );
}

export function GuideEntry({
  returnTo,
  compact = false,
}: {
  returnTo: '/settings' | '/decode';
  compact?: boolean;
}) {
  return (
    <StonePanel className="guide-entry" data-compact={compact || undefined}>
      <span className="realm-screen__eyebrow">Player guide</span>
      <h2>{compact ? 'Need a reminder?' : 'How to play'}</h2>
      {!compact && (
        <p>Replay Explore → Decode → reward → Build with captions and practice examples.</p>
      )}
      <Link className="guide-entry__link" to={`/guide?replay=1&return=${returnTo}`}>
        {compact ? 'Replay the puzzle lesson' : 'Open the guided path'}
      </Link>
    </StonePanel>
  );
}
